import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import clock from "../state/clock";
import { getNumber, getText, getWidgetNumber, getWidgetText } from "../state/modifications";
import escapeIdentifier from "./escapeIdentifier";
import { ExpressionHandler, ScalarValue } from "./ExpressionTypes";
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { noConversion } from "./number_converter";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_modifications = api_configuration.types.modifications.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import client_state_binder = require("./client_state_binder");
import context_selection = require("./context_selection");
import { Transformers } from "./ClientBindings";
import { visitOneOrMoreExpression } from "./visit_widget_implementation_expression";
import { expressionIdGenerator } from "./visit_widget_implementation_node";
import { getTranslation, subscribeToTranslation } from "../state/phrases";

export function visitScalar(scalar: api_widget.Cscalar__widget): ExpressionHandler<string | number> {
	let binding_handler: ExpressionHandler<string | number> | undefined = undefined;
	switch (scalar.properties.binding_type.state.name) {
		case "binding": {
			const binding = scalar.properties.binding_type.state.node;
			binding_handler = bindingHanlder(binding);
			break;
		}
		case "configuration": {
			const configuration = scalar.properties.binding_type.state.node;
			switch (configuration.properties.type.state.name) {
				case "number": {
					const number__configuration = configuration.properties.type.state.node;
					binding_handler = numberConfigurationHandler(number__configuration);
					break;
				}
				case "text": {
					const text__configuration = configuration.properties.type.state.node;
					binding_handler = textConfigurationHandler(text__configuration);
					break;
				}
				default:
					unexpectedState(configuration.properties.type.state);
			}
			break;
		}
		case "current time": {
			const current_time = scalar.properties.binding_type.state.node;
			binding_handler = currentTimeHandler(current_time);
			break;
		}
		case "event": {
			const event = scalar.properties.binding_type.state.node;
			switch (event.properties.type.state.name) {
				case "number": {
					binding_handler = {
						execute: async (_, _d, e: number) => e,
						subscribe: (implementation_context, callback, destructor, _generate_key, e: number) => {
							callback.add(e, implementation_context, destructor, false);
						},
					};
					break;
				}
				case "text": {
					binding_handler = {
						execute: async (_, _d, e: string) => e,
						subscribe: (implementation_context, callback, destructor, _generate_key, e: string) => {
							callback.add(e, implementation_context, destructor, false);
						},
					};
					break;
				}
				default:
					unexpectedState(event.properties.type.state);
			}
			break;
		}
		case "list index": {
			const list_index = scalar.properties.binding_type.state.node;
			binding_handler = listIndexHandler(list_index);
			break;
		}
		case "phrase": {
			const phrase = scalar.properties.binding_type.state.node;
			binding_handler = phraseHandler(phrase);
			break;
		}
		case "static number": {
			const static_number = scalar.properties.binding_type.state.node;
			binding_handler = staticValueHandler(static_number.properties.value.value);
			break;
		}
		case "static text": {
			const static_text = scalar.properties.binding_type.state.node;
			binding_handler = staticValueHandler(static_text.properties.text);
			break;
		}
		case "unary expression": {
			const unary_expression = scalar.properties.binding_type.state.node;
			binding_handler = unaryExpressionHandler(unary_expression);
			break;
		}
		case "list expression": {
			const list_expression = scalar.properties.binding_type.state.node;
			binding_handler = listExpressionHandler(list_expression);
			break;
		}
		default:
			return unexpectedState(scalar.properties.binding_type.state);
	}
	switch (scalar.properties.transform.state.name) {
		case "no":
			break;
		case "yes": {
			const $ = scalar.properties.transform.state.node;
			binding_handler = transformationHandler($, binding_handler);
			break;
		}
		default:
			unexpectedState(scalar.properties.transform.state);
	}
	switch (scalar.properties.format.state.name) {
		case "no":
			break;
		case "yes": {
			const $ = scalar.properties.format.state.node;
			binding_handler = formattingHandler($, binding_handler);
			break;
		}
		default:
			unexpectedState(scalar.properties.format.state);
	}
	return binding_handler;
}

function transformationHandler(
	convert: api_widget.Cyes__transform,
	wrapped_handler: ExpressionHandler<string | number>
): ExpressionHandler<string | number> {
	const transformer: (value: string | number) => number =
		client_state_binder.transformers[convert.properties.transformer.entry as keyof Transformers];
	return {
		execute: async (implementation_context, destructor, event_value) => {
			const value = (await wrapped_handler.execute(implementation_context, destructor, event_value)) as string | number;
			return transformer(value);
		},
		subscribe: (implementation_context, callback, destructor, event_value) => {
			wrapped_handler.subscribe(
				implementation_context,
				{
					add: (value: string | number, ic, d, wrap) => {
						callback.add(transformer(value), ic, d, wrap);
					},
					update: callback.update,
				},
				destructor,
				event_value
			);
		},
	};
}

function formattingHandler(
	format_type: api_widget.Cyes__format,
	wrapped_handler: ExpressionHandler<string | number>
): ExpressionHandler<string | number> {
	const formatter = (client_state_binder.formatters as any)[format_type.properties.formatter.entry];
	return {
		execute: async (implementation_context, destructor, event_value) => {
			return formatter(wrapped_handler.execute(implementation_context, destructor, event_value));
		},
		subscribe: (implementation_context, callback, destructor, event_value) => {
			wrapped_handler.subscribe(
				implementation_context,
				{
					add: (value, ic, d, wrap) => {
						callback.add(formatter(value), ic, d, wrap);
					},
					update: callback.update,
				},
				destructor,
				event_value
			);
		},
	};
}

function staticValueHandler<T extends ScalarValue>(value: T): ExpressionHandler<T> {
	return {
		execute: async () => value,
		subscribe: (implementation_context, callback, destructor) => {
			callback.add(value, implementation_context, destructor, false);
		},
	};
}

function listIndexHandler(list_index: api_widget.Clist_index): ExpressionHandler<number> {
	return {
		execute: async (implementation_context) => getListIndex(list_index, implementation_context),
		subscribe: (implementation_context, callback, destructor) => {
			callback.add(getListIndex(list_index, implementation_context), implementation_context, destructor, false);
		},
	};
}

function getListIndex(
	list_index: api_widget.Clist_index,
	implementation_context: ImplementationVisitorContext
): number {
	const destructor = DestructorTree.create();
	const list_context = context_selection.context_selection(
		list_index.properties.context,
		implementation_context,
		destructor
	);
	destructor.destroy();
	const list_counter = ( //FIXME: do the location cast in the schema
		list_context.gui_widget_configuration.location as api_views.Cyes__has_steps__gui_widget_configuration_list
	).definitions.list_counter;
	return numberindex(list_counter);
}

function numberindex(list_counter: api_views.Clist_counter): number {
	return list_counter.switch({
		start: 0,
		entry: ($) => numberindex($.parent.input.previous_entry()) + 1,
	});
}

function currentTimeHandler(current_time: api_widget.Ccurrent_time): ExpressionHandler<number> {
	let current_time_throttle: ((callback: (value: number) => void) => (value: number) => void) | undefined = undefined;
	switch (current_time.properties.throttle.state.name) {
		case "no": {
			current_time_throttle = (callback) => callback;
			break;
		}
		case "yes": {
			const $ = current_time.properties.throttle.state.node;
			current_time_throttle = (callback) => throttle(callback, $.properties.interval.value);
			break;
		}
		default:
			unexpectedState(current_time.properties.throttle.state);
	}
	return {
		execute: async () => clock.getNow(),
		subscribe: (implementation_context, callback, destructor) => {
			const clock_callback = current_time_throttle!((time: number) => {
				callback.add(time, implementation_context, destructor, false);
			});
			clock.on("tick", clock_callback);
			destructor.onDestroy(() => {
				clock.off("tick", clock_callback);
			});
		},
	};
}

type NumberCallback = (value: number) => void;
function throttle(callback: NumberCallback, interval: number): NumberCallback {
	let executing = false;
	let reexecute = false;
	let last_value: number;

	return function execute(value: number) {
		if (executing) {
			reexecute = true;
			last_value = value;
			return;
		}
		executing = true;
		callback(value);
		setTimeout(function () {
			executing = false;
			if (reexecute) {
				reexecute = false;
				execute(last_value);
			}
		}, interval * 1000);
	};
}

function bindingHanlder(expression: api_widget.Cbinding__binding_type): ExpressionHandler<string | number> {
	switch (expression.properties.type.state.name) {
		case "key": {
			return {
				execute: async (implementation_context) => {
					const destructor = DestructorTree.create();
					const binding_context = context_selection.context_selection(
						expression.properties.context,
						implementation_context,
						destructor
					);
					destructor.destroy();
					return (binding_context.current_binding_context as unknown as api_client_state.AlanDictionaryEntry).key_value;
				},
				subscribe: (implementation_context, callback, destructor) => {
					const binding_context = context_selection.context_selection(
						expression.properties.context,
						implementation_context,
						DestructorTree.create()
					);
					const key_value = (binding_context.current_binding_context as unknown as api_client_state.AlanDictionaryEntry)
						.key_value;
					callback.add(key_value, binding_context, destructor, false);
				},
			};
		}
		case "property": {
			const property = expression.properties.type.state.node;
			const property_name = property.properties.property.entry;
			const escaped_property_name = escapeIdentifier(property_name);
			switch (property.properties.type.state.name) {
				case "number": {
					return {
						execute: async (implementation_context) => {
							const destructor = DestructorTree.create();
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							destructor.destroy();
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							const converter = property_context.number_converter ?? noConversion;
							return converter.onceTo(
								(
									property_context.current_binding_context.properties[
										escaped_property_name
									] as api_client_state.AlanInteger
								).value
							);
						},
						subscribe: (implementation_context, callback, destructor) => {
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							const converter = (property_context.number_converter ?? noConversion).to(destructor);
							destructor.subscribeToNumber(
								property_context.current_binding_context as any,
								("subscribe_" + escaped_property_name) as any,
								(value, property_destructor) => {
									converter(value, (result) => {
										callback.add(result, property_context, property_destructor, false);
									});
								},
								true
							);
						},
					};
				}
				case "reference": {
					return {
						execute: async (implementation_context) => {
							const destructor = DestructorTree.create();
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							destructor.destroy();
							return (
								property_context.current_binding_context.properties[
									escaped_property_name
								] as api_client_state.Reference<any, string>
							).entry;
						},
						subscribe: (implementation_context, callback, destructor) => {
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							destructor.subscribeToText(
								property_context.current_binding_context as any,
								("subscribe_" + escaped_property_name) as any,
								(value, property_destructor) => {
									callback.add(value, binding_context, property_destructor, false);
								},
								true
							);
						},
					};
				}
				case "text": {
					return {
						execute: async (implementation_context) => {
							const destructor = DestructorTree.create();
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							destructor.destroy();
							return property_context.current_binding_context.properties[escaped_property_name] as string;
						},
						subscribe: (implementation_context, callback, destructor) => {
							const binding_context = context_selection.context_selection(
								expression.properties.context,
								implementation_context,
								destructor
							);
							const property_context = binding_context.switchClientStateBinderToProperty(property_name, destructor);
							destructor.subscribeToText(
								property_context.current_binding_context as any,
								("subscribe_" + escaped_property_name) as any,
								(value, property_destructor) => {
									callback.add(value, property_context, property_destructor, false);
								},
								true
							);
						},
					};
				}
				default:
					return unexpectedState(property.properties.type.state);
			}
		}
		default:
			unexpectedState(expression.properties.type.state);
	}
}

function numberConfigurationHandler(
	number__type: api_widget.Cnumber__type__configuration__binding_type
): ExpressionHandler<number> {
	return {
		execute: async (implementation_context) => {
			const destructor = DestructorTree.create();
			const number_implementation_context = context_selection.context_selection(
				number__type.parent.properties.context,
				implementation_context,
				destructor
			);
			destructor.destroy();
			const widget_configuration = number_implementation_context.widget_configuration;
			const node_context = number_implementation_context.view_context_node;
			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const number__type__configuration = widget_configuration.properties.configuration
					.get(number__type.parent.properties.attribute.entry)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("number");
				if (number__type__configuration === undefined) {
					const widget_number = number__type.inferences.number_type().properties.has_default.cast("yes");
					const number_value = getWidgetNumber(
						node_context,
						number_implementation_context.last_gui_widget_configuration,
						widget_number.parent,
						number_implementation_context.entry_keys
					);
					if (number_value.value !== undefined) {
						return number_value.value;
					} else {
						return widget_number.properties.value.value;
					}
				} else {
					const number_value = getNumber(
						node_context,
						number__type__configuration,
						number_implementation_context.entry_keys
					);
					const current_value =
						number_value.value ??
						number__type__configuration.properties.source.switch({
							now: ($) =>
								clock.getNow() +
								$.properties.has_offset.switch({
									none: 0,
									yes: ($) => $.properties.offset.value,
								}),
							static: ($) => $.properties.number.value,
						});
					return current_value;
				}
			} else if (widget_configuration instanceof api_modifications.Cconfiguration_node) {
				const number__type__configuration = widget_configuration.properties.attributes
					.get(number__type.parent.properties.attribute.entry)!
					.properties.type.cast("number");
				return number__type__configuration.properties.value.value;
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				const widget_number = number__type.inferences.number_type().properties.has_default.cast("yes");
				const number_value = getWidgetNumber(
					node_context,
					number_implementation_context.last_gui_widget_configuration,
					widget_number.parent,
					number_implementation_context.entry_keys
				);
				if (number_value.value !== undefined) {
					return number_value.value;
				} else {
					return widget_number.properties.value.value;
				}
			} else {
				unexpectedState(widget_configuration);
			}
		},
		subscribe: (implementation_context, callback, destructor) => {
			const number_implementation_context = context_selection.context_selection(
				number__type.parent.properties.context,
				implementation_context,
				destructor
			);
			const onNumberValue = (value: number) => {
				callback.add(value, number_implementation_context, destructor, false);
			};
			const widget_configuration = number_implementation_context.widget_configuration;
			const node_context = number_implementation_context.view_context_node;
			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const number__type__configuration = widget_configuration.properties.configuration
					.get(number__type.parent.properties.attribute.entry)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("number");
				if (number__type__configuration === undefined) {
					const widget_number = number__type.inferences.number_type().properties.has_default.cast("yes");
					const number_value = getWidgetNumber(
						node_context,
						number_implementation_context.last_gui_widget_configuration,
						widget_number.parent,
						number_implementation_context.entry_keys
					);
					if (number_value.value !== undefined) {
						onNumberValue(number_value.value);
					} else {
						onNumberValue(widget_number.properties.value.value);
					}
					const subscription = number_value.subscribe((new_value) => {
						onNumberValue(new_value);
					});
					destructor.onDestroy(() => {
						subscription.unsubscribe();
					});
				} else {
					const number_value = getNumber(
						node_context,
						number__type__configuration,
						number_implementation_context.entry_keys
					);
					const current_value =
						number_value.value ??
						number__type__configuration.properties.source.switch({
							now: ($) =>
								clock.getNow() +
								$.properties.has_offset.switch({
									none: 0,
									yes: ($) => $.properties.offset.value,
								}),
							static: ($) => $.properties.number.value,
						});
					onNumberValue(current_value);
					const subscription = number_value.subscribe((new_value) => {
						onNumberValue(new_value);
					});
					destructor.onDestroy(() => {
						subscription.unsubscribe();
					});
				}
			} else if (widget_configuration instanceof api_modifications.Cconfiguration_node) {
				const number__type__configuration = widget_configuration.properties.attributes
					.get(number__type.parent.properties.attribute.entry)!
					.properties.type.cast("number");
				onNumberValue(number__type__configuration.properties.value.value);
				destructor.subscribeToNumber(
					number__type__configuration as any,
					"subscribe_value" as any,
					(value) => {
						onNumberValue(value);
					},
					true
				);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				const widget_number = number__type.inferences.number_type().properties.has_default.cast("yes");
				const number_value = getWidgetNumber(
					node_context,
					number_implementation_context.last_gui_widget_configuration,
					widget_number.parent,
					number_implementation_context.entry_keys
				);
				if (number_value.value !== undefined) {
					onNumberValue(number_value.value);
				} else {
					onNumberValue(widget_number.properties.value.value);
				}
				const subscription = number_value.subscribe((new_value) => {
					onNumberValue(new_value);
				});
				destructor.onDestroy(() => {
					subscription.unsubscribe();
				});
			} else {
				unexpectedState(widget_configuration);
			}
		},
	};
}

function textConfigurationHandler(
	text__type: api_widget.Ctext__type__configuration__binding_type
): ExpressionHandler<string> {
	return {
		execute: async (implementation_context) => {
			const destructor = DestructorTree.create();
			const text_implementation_context = context_selection.context_selection(
				text__type.parent.properties.context,
				implementation_context,
				destructor
			);
			destructor.destroy();

			const bound_text_key = text__type.parent.properties.attribute.entry;
			const widget_configuration = text_implementation_context.widget_configuration;
			const node_context = text_implementation_context.view_context_node;

			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const configuration_text = widget_configuration.properties.configuration
					.get(bound_text_key)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("text");
				if (configuration_text === undefined) {
					const widget_text = widget_configuration.input
						.widget_configuration_node()
						.properties.attributes.get(bound_text_key)!
						.properties.type.cast("configuration")
						.properties.type.cast("text")
						.properties.has_default.cast("yes");

					const text_value = getWidgetText(
						node_context,
						widget_configuration,
						widget_text.parent,
						text_implementation_context.entry_keys
					);
					if (text_value.value !== undefined) {
						return text_value.value;
					} else {
						switch (widget_text.properties.type.state.name) {
							case "static": {
								const $ = widget_text.properties.type.state.node;
								return $.properties.value;
							}
							case "unique id": {
								const $ = widget_text.properties.type.state.node;
								return text_implementation_context.uniqueId($);
							}
							default:
								unexpectedState(widget_text.properties.type.state);
						}
					}
				} else {
					const text_value = getText(node_context, configuration_text, text_implementation_context.entry_keys);
					if (text_value.value !== undefined) {
						return text_value.value;
					}
					switch (configuration_text.properties.type.state.name) {
						case "static":
							return configuration_text.properties.type.state.node.properties.value;
						case "phrase": {
							const gui_definition_configuration_text_value =
								configuration_text.properties.type.state.node.properties.value.ref;
							const phrase = gui_definition_configuration_text_value.key;
							return getTranslation(phrase);
						}
						case "key": {
							const key_type = configuration_text.properties.type.state.node;
							const key = switchKeyContext(key_type);
							switch (key_type.properties.translate.state.name) {
								case "no": {
									return key;
								}
								case "yes": {
									return getTranslation(key);
								}
								default:
									return unexpectedState(key_type.properties.translate.state);
							}
						}
						default:
							unexpectedState(configuration_text.properties.type.state);
					}
				}
			} else if (widget_configuration instanceof api_modifications.Cconfiguration_node) {
				const configuration_text = widget_configuration.properties.attributes
					.get(bound_text_key)!
					.properties.type.cast("text");
				return configuration_text.properties.value;
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				const widget_text = widget_configuration.properties.attributes
					.get(bound_text_key)!
					.properties.type.cast("configuration")
					.properties.type.cast("text")
					.properties.has_default.cast("yes");
				const text_value = getWidgetText(
					node_context,
					text_implementation_context.last_gui_widget_configuration,
					widget_text.parent,
					text_implementation_context.entry_keys
				);
				if (text_value.value !== undefined) {
					return text_value.value;
				} else {
					switch (widget_text.properties.type.state.name) {
						case "static": {
							const $ = widget_text.properties.type.state.node;
							return $.properties.value;
						}
						case "unique id": {
							const $ = widget_text.properties.type.state.node;
							return text_implementation_context.uniqueId($);
						}
						default:
							unexpectedState(widget_text.properties.type.state);
					}
				}
			} else {
				unexpectedState(widget_configuration);
			}
		},
		subscribe: (implementation_context, callback, destructor) => {
			const text_implementation_context = context_selection.context_selection(
				text__type.parent.properties.context,
				implementation_context,
				destructor
			);
			const onTextValue = (value: string) => {
				callback.add(value, text_implementation_context, destructor, false);
			};

			const bound_text_key = text__type.parent.properties.attribute.entry;
			const widget_configuration = text_implementation_context.widget_configuration;
			const node_context = text_implementation_context.view_context_node;

			const subscribeWidgetText = (
				node: api_widget.Cwidget_configuration_node,
				gui_node: api_views.Cgui_widget_configuration_node
			) => {
				const widget_text = node.properties.attributes
					.get(bound_text_key)!
					.properties.type.cast("configuration")
					.properties.type.cast("text")
					.properties.has_default.cast("yes");

				const text_value = getWidgetText(
					node_context,
					gui_node,
					widget_text.parent,
					text_implementation_context.entry_keys
				);
				if (text_value.value !== undefined) {
					onTextValue(text_value.value);
				} else {
					switch (widget_text.properties.type.state.name) {
						case "static": {
							const $ = widget_text.properties.type.state.node;
							onTextValue($.properties.value);
							break;
						}
						case "unique id": {
							const $ = widget_text.properties.type.state.node;
							onTextValue(text_implementation_context.uniqueId($));
							break;
						}
						default:
							unexpectedState(widget_text.properties.type.state);
					}
				}
				unsubscribeOnDestroy(text_value.subscribe(onTextValue), destructor);
			};

			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const configuration_text = widget_configuration.properties.configuration
					.get(bound_text_key)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("text");
				if (configuration_text === undefined) {
					subscribeWidgetText(widget_configuration.input.widget_configuration_node(), widget_configuration);
				} else {
					let text_modified = false;
					const text_value = getText(node_context, configuration_text, text_implementation_context.entry_keys);
					if (text_value.value !== undefined) {
						text_modified = true;
						onTextValue(text_value.value);
					}
					unsubscribeOnDestroy(
						text_value.subscribe((value) => {
							onTextValue(value);
							text_modified = true;
						}),
						destructor
					);

					switch (configuration_text.properties.type.state.name) {
						case "static":
							if (!text_modified) {
								onTextValue(configuration_text.properties.type.state.node.properties.value);
							}
							break;
						case "phrase": {
							const gui_definition_configuration_text_value =
								configuration_text.properties.type.state.node.properties.value.ref;
							const phrase = gui_definition_configuration_text_value.key;
							subscribeToTranslation(phrase, destructor, (translation) => {
								if (!text_modified) {
									onTextValue(translation);
								}
							});
							break;
						}
						case "key": {
							const key_type = configuration_text.properties.type.state.node;
							const key = switchKeyContext(key_type);
							switch (key_type.properties.translate.state.name) {
								case "no": {
									onTextValue(key);
									break;
								}
								case "yes": {
									subscribeToTranslation(key, destructor, (translation) => {
										if (!text_modified) {
											onTextValue(translation);
										}
									});
									break;
								}
								default:
									unexpectedState(key_type.properties.translate.state);
							}
							break;
						}
						default:
							unexpectedState(configuration_text.properties.type.state);
					}
				}
			} else if (widget_configuration instanceof api_modifications.Cconfiguration_node) {
				const configuration_text = widget_configuration.properties.attributes
					.get(bound_text_key)!
					.properties.type.cast("text");
				destructor.subscribeToText(
					configuration_text,
					"subscribe_value",
					(value) => {
						onTextValue(value);
					},
					true
				);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				subscribeWidgetText(widget_configuration, text_implementation_context.last_gui_widget_configuration);
			} else {
				unexpectedState(widget_configuration);
			}
		},
	};
}

function switchKeyContext(key_type: api_views.Ckey) {
	let key = "";
	const key_is_set = key_type.inferences.is_set();
	switch (key_is_set.variant.name) {
		case "graph": {
			key = key_is_set.variant.definition.inferences.ordered_graph().parent.key_value;
			break;
		}
		case "rule": {
			key = key_is_set.variant.definition.key_value;
			break;
		}
		case "state": {
			key = key_is_set.variant.definition.key_value;
			break;
		}
		case "attribute": {
			key = key_is_set.variant.definition.key_value;
			break;
		}
		default:
			unexpectedState(key_is_set.variant);
	}
	return key;
}

function unsubscribeOnDestroy(subscription: { unsubscribe: () => void }, destructor: DestructorTree): void {
	destructor.onDestroy(() => {
		subscription.unsubscribe();
	});
}

function phraseHandler(phrase_type: api_widget.Cphrase): ExpressionHandler<string> {
	const phrase = phrase_type.properties.phrase.entry;
	return {
		execute: async () => {
			return getTranslation(phrase);
		},
		subscribe: (implementation_context, callback, destructor) => {
			subscribeToTranslation(phrase, destructor, (translation) => {
				callback.add(translation, implementation_context, destructor, false);
			});
		},
	};
}

function unaryExpressionHandler(unary_expression: api_widget.Cunary_expression): ExpressionHandler<string | number> {
	const process_value = unary_expression.properties.type.switch<(value: number) => number>({
		"absolute value": () => {
			return (value: number) => Math.abs(value);
		},
		"sign inversion": () => {
			return (value: number) => -value;
		},
	});
	const scalar_expression = visitScalar(unary_expression.properties.value);

	return {
		execute: async (implementation_context, destructor, event_value) => {
			const expression_value = await scalar_expression.execute(implementation_context, destructor, event_value);
			return process_value(expression_value as number);
		},
		subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
			scalar_expression.subscribe(
				implementation_context,
				{
					add: (value, ic, d, w, key) => {
						callback.add(process_value(value as number), ic, d, w, key);
					},
					update: (sorting) => {
						callback.update?.(sorting);
					},
				},
				destructor,
				generate_key,
				event_value
			);
		},
	};
}

function listExpressionHandler(list_expression: api_widget.Clist_expression): ExpressionHandler<string | number> {
	const process_values = list_expression.properties.operation.switch<(values: number[]) => number>({
		maximum: () => (values: number[]) => {
			return Math.max(...values);
		},
		minimum: () => (values: number[]) => {
			return Math.min(...values);
		},
		product: () => (values: number[]) => {
			if (values.length === 0) {
				return 0;
			}
			return values.reduce((p, c) => p * c);
		},
		sum: () => (values: number[]) => {
			return values.reduce((p, c) => p + c, 0);
		},
	});
	const expression = visitOneOrMoreExpression(list_expression.properties.value);

	return {
		execute: async (implementation_context, destructor, event_value) => {
			const expression_result = await expression.execute(implementation_context, destructor, event_value);
			const result = expression_result instanceof Array ? expression_result : [expression_result];
			return process_values(
				result.filter((v) => {
					return v !== undefined;
				}) as number[]
			);
		},
		subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
			const values = new Map<string | undefined, number>();
			const list_expression_key = generate_key?.();
			let first = true;
			const update = () => {
				if (destructor.destroyed) return;
				first = false;
				callback.add(
					process_values([...values.values()]),
					implementation_context,
					destructor,
					false,
					list_expression_key
				);
			};

			expression.subscribe(
				implementation_context,
				{
					add: (value: number, ic, d, w, key) => {
						if (value === undefined) {
							return;
						}
						values.set(key, value);
						d.onDestroy(() => {
							values.delete(key);
							update();
						});
						update();
					},
					update: () => {
						// do nothing
					},
				},
				destructor,
				expressionIdGenerator(),
				event_value
			);
			if (first) {
				update();
			}
		},
	};
}
